import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {MatDialogModule} from '@angular/material/dialog';
import {CommonModule} from '@angular/common';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {DialogCloseButtonComponent, IconButtonComponent} from '@q9elements/ui-kit/components';
import {ConfigService, Q9HtmlEncoderPipe} from '@q9elements/ui-kit/common';
import {
  IActionMapping,
  ITreeOptions,
  KEYS,
  TreeComponent,
  TreeNode,
  TreeModule
} from 'ng-tree-component';
import {Subscription} from 'rxjs';
import {TranslateModule} from '@ngx-translate/core';
import {MapTreeService} from '../../../core/services/map-tree.service';
import {NavigationService} from '../../../core/navigation/navigation.service';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {MapTreeStateService} from '../../../core/services/map-tree-state.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../shared/models/diagram-version.constant';
import {VersionsService} from '../../../core/services/versions.service';
import {MapTreeNodeComponent} from './map-tree-node/map-tree-node.component';
import {ResizableDialogDirective} from '../../shared/directives/resizable-dialog.directive';
import {DraggableDialogDirective} from '../../shared/directives/draggable-dialog.directive';

@Component({
  selector: 'app-map-tree-dialog',
  templateUrl: './map-tree-dialog.component.html',
  styleUrls: ['./map-tree-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    DraggableDialogDirective,
    ResizableDialogDirective,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    TreeModule,
    MapTreeNodeComponent,
    TranslateModule,
    DialogCloseButtonComponent,
    IconButtonComponent,
    Q9HtmlEncoderPipe
  ]
})
export class MapTreeDialogComponent implements OnInit, OnDestroy {
  private actions: IActionMapping = {
    mouse: {
      click: (model, node, evt) => {
        if (this.processing) {
          return;
        }

        this.processing = true;
        this.navSvc.relative(node.data.id).then(() => this.versionsSvc.getMapAuthorisationMode());
      },
      drop: null
    },
    keys: {
      [KEYS.SPACE]: null,
      [KEYS.ENTER]: null
    }
  };
  public processing = false;
  public valid: boolean;
  public nodes = [];
  public title: string;
  public triggerSub: Subscription;
  public shouldSendLog = false;
  public noItemsImageUrl = `${ConfigService.environment.STATIC_FILES_URL}/icons/placeholders/empty.svg`;

  public treeOpts: ITreeOptions = {
    actionMapping: this.actions,
    getChildren: node =>
      this.mapSvc
        .diagramChildren(node.data.id, node.data.version)
        .toPromise()
        .then(data => this.prepareOrder(data || []))
        .catch(e => {
          this.treeComponent.treeModel.setState({}); // TODO: prune only descendant states to prevent deleting valid state data
          this.loadPath();
        })
  };

  @ViewChild('tree', {static: true}) treeComponent: TreeComponent;

  constructor(
    private mapSvc: MapTreeService,
    private jsonHandlerSvc: DiagramJsonHandlerService,
    private navSvc: NavigationService,
    private versionsSvc: VersionsService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadPath().then(() => this.loadState());
    this.triggerSub = this.mapSvc.updateTreeTrigger$.subscribe(() => this.loadPath());
    this.shouldSendLog = !this.shouldSendLog;
  }

  ngOnDestroy() {
    this.triggerSub.unsubscribe();
    this.saveState();
  }

  saveState() {
    if (this.rootId) {
      const data = {
        uuid: this.rootId,
        state: this.treeComponent.treeModel.getState()
      };

      MapTreeStateService.save(data);
    }
  }

  loadState() {
    const data = MapTreeStateService.load();

    if (data) {
      if (data.uuid === this.rootId && data.state) {
        this.treeComponent.treeModel.setState(data.state);
      } else {
        this.wipeState();
      }
    }
  }

  wipeState() {
    this.treeComponent.treeModel.setState({});
    MapTreeStateService.wipe();
  }

  get rootId() {
    const root = this.nodes[0];

    if (root) {
      return root.uuid;
    }
  }

  get prevRootId() {
    const data = MapTreeStateService.load();

    if (data) {
      return data.uuid;
    }
  }

  treeUpdate() {
    this.highlightActiveNode();
    this.processing = false;
    this.cd.detectChanges();
  }

  private generateTitle(data) {
    const names = this.jsonHandlerSvc.namings;
    const isPro = this.jsonHandlerSvc.isPro();

    return (
      "of '" +
      names.mapName +
      "'." +
      (isPro ? ' Version: ' + this.getPrettyMapVer(data.version) : '')
    );
  }

  private highlightActiveNode() {
    const curr = this.navSvc.getCurrent();

    this.highlightNode(curr.id);
  }

  private highlightNode(id: string) {
    const node: TreeNode = this.treeComponent.treeModel.getNodeById(id);

    if (node) {
      node.setActiveAndVisible();
      node.expand();
      this.valid = true;
    } else {
      this.valid = false;
    }
  }

  private loadPath() {
    this.processing = true;
    const curr = this.navSvc.getCurrent();

    if (this.rootId && this.prevRootId && this.rootId !== this.prevRootId) {
      this.wipeState();
    }

    return this.mapSvc
      .partialTree(curr.id, curr.state)
      .toPromise()
      .then(data => {
        this.title = this.generateTitle(data);
        data.children = this.prepareOrderForChildren(data.children || []);
        this.nodes = [data];
      })
      .catch(e => {
        this.valid = false;
        this.processing = false;
        this.wipeState();
      });
  }

  private getPrettyMapVer(version) {
    if (version === DIAGRAM_VERSION_STATES_ENUM.MASTER) {
      version = 'current';
    }

    return version.startsWith('r_')
      ? 'Archived'
      : version.charAt(0).toUpperCase() + version.slice(1);
  }

  private prepareOrderForChildren(nodes) {
    nodes = this.prepareOrder(nodes);
    nodes.map(node => {
      if (node.children) {
        node.children = this.prepareOrderForChildren(node.children);

        return node;
      }
    });

    return nodes;
  }

  private prepareOrder(nodes) {
    nodes.sort((a, b) => {
      const nums1 = a.level.split('.');
      const nums2 = b.level.split('.');

      for (let i = 0; i < nums1.length; i++) {
        if (nums2[i]) {
          if (nums1[i] !== nums2[i]) {
            return nums1[i] - nums2[i];
          }
        } else {
          return 1;
        }
      }

      return -1;
    });

    return nodes;
  }
}
